'use client'

import React, { useState, useEffect } from 'react'
import { Video, Users, DollarSign, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { HostCourseForm } from '../forms/HostCourseForm'
import { createPortal } from 'react-dom'

function HostCourseSection() {
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false)

  // Handle body scroll locking when modal is open
  useEffect(() => {
    if (isApplicationFormOpen) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden'
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = ''
    }

    // Cleanup function to ensure scrolling is re-enabled if component unmounts
    return () => {
      document.body.style.overflow = ''
    }
  }, [isApplicationFormOpen])

  const openApplicationForm = () => {
    setIsApplicationFormOpen(true)
  }

  const closeApplicationForm = () => {
    setIsApplicationFormOpen(false)
  }

  return (
    <div className="container mx-auto px-4 py-12 sm:py-24">
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-3xl p-8 sm:p-12 md:p-16 shadow-xl">
        <div className="flex flex-col lg:flex-row items-center">
          <div className="lg:w-1/2 mb-8 lg:mb-0 lg:pr-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 text-gray-800">Host Your Course</h2>
            <p className="text-gray-600 mb-6 md:mb-8 text-base sm:text-lg md:text-xl">
              Share your expertise with our global community. Create engaging courses and help others succeed in their learning journey.
            </p>
            <div className="space-y-4 md:space-y-6 mb-6 md:mb-10">
              {[
                { icon: Video, text: 'Create and upload video lessons' },
                { icon: Users, text: 'Reach thousands of eager learners' },
                { icon: DollarSign, text: 'Earn money from your expertise' }
              ].map((item, index) => (
                <div key={index} className="flex items-center">
                  <item.icon className="text-primary mr-3 md:mr-4" size={24} />
                  <span className="text-base md:text-lg">{item.text}</span>
                </div>
              ))}
            </div>
            
            <motion.button 
              className="bg-primary text-white font-bold py-3 rounded-full w-full md:w-auto px-6 md:px-8 transition-all duration-300 hover:bg-primary/90 shadow-md hover:shadow-lg text-base md:text-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={openApplicationForm}
            >
              Apply to Host a Course
            </motion.button>
          </div>
          <div className="lg:w-1/2">
            <img 
              src="https://i.ibb.co/V0NZhhWQ/Host-ourses-3.webp" 
              alt="Host teaching online" 
              className="rounded-2xl shadow-2xl w-full h-auto object-cover transform hover:scale-105 transition duration-300"
            />
          </div>
        </div>
      </div>
      
      {/* Application Form Modal */}
      {isApplicationFormOpen && createPortal(
        <AnimatePresence>
          <motion.div 
            className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black bg-opacity-60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeApplicationForm}
          >
            <motion.div 
              className="relative max-w-md w-full bg-white rounded-xl shadow-xl overflow-hidden"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="absolute top-3 right-3 md:top-4 md:right-4 text-gray-500 hover:text-gray-700 focus:outline-none z-[10000]"
                onClick={closeApplicationForm}
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
              <div className="p-4 md:p-6">
                <h2 className="text-2xl font-bold text-center mb-4">Apply to Host a Course</h2>
                <HostCourseForm 
                  title="" 
                  isModal={true} 
                  onClose={closeApplicationForm}
                  onSuccess={() => setTimeout(closeApplicationForm, 2000)}
                />
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </div>
  )
}

export default HostCourseSection
